/**
 * Dashboard.js
 * Main dashboard component with statistics and overview
 */

import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Alert,
  Spinner,
  ListGroup,
  Badge
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { dashboardService, userService, groupService } from '../../services/api';
import { countValidSubscriptions } from '../../helpers/subscriptionHelpers';
import FilterSelect from '../Common/FilterSelect';
import DailyRollCallsCalendar from './DailyRollCallsCalendar';
import CoachScheduleSection from './CoachScheduleSection';

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalExpiringMedicalCertificates: 0,
    totalExpiredMedicalCertificates: 0,
    totalMissingMedicalCertificates: 0,
    currentAcademicYearSubscriptions: 0,
    currentAcademicYearCourses: 0,
    currentAcademicYearGroups: 0,
    totalExpiringSubscriptions: 0,
    totalExpiredSubscriptions: 0,
    totalRunningOutSubscriptions: 0,
    totalSoldOutSubscriptions: 0,
    totalUnpaidSubscriptions: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Load dashboard data
  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load dashboard statistics from GraphQL
      const dashboardResponse = await dashboardService.getStats();
      if (dashboardResponse.success) {
        setStats({
          totalUsers: dashboardResponse.data.totalUsers || 0,
          totalExpiringMedicalCertificates: dashboardResponse.data.totalExpiringMedicalCertificates || 0,
          totalExpiredMedicalCertificates: dashboardResponse.data.totalExpiredMedicalCertificates || 0,
          totalMissingMedicalCertificates: dashboardResponse.data.totalMissingMedicalCertificates || 0,
          currentAcademicYearSubscriptions: dashboardResponse.data.currentAcademicYearSubscriptions || 0,
          currentAcademicYearCourses: dashboardResponse.data.currentAcademicYearCourses || 0,
          currentAcademicYearGroups: dashboardResponse.data.currentAcademicYearGroups || 0,
          totalExpiringSubscriptions: dashboardResponse.data.totalExpiringSubscriptions || 0,
          totalExpiredSubscriptions: dashboardResponse.data.totalExpiredSubscriptions || 0,
          totalRunningOutSubscriptions: dashboardResponse.data.totalRunningOutSubscriptions || 0,
          totalSoldOutSubscriptions: dashboardResponse.data.totalSoldOutSubscriptions || 0,
          totalUnpaidSubscriptions: dashboardResponse.data.totalUnpaidSubscriptions || 0
        });
      } else {
        setError(dashboardResponse.message || 'Errore nel caricamento delle statistiche');
      }

    } catch (err) {
      setError('Errore nel caricamento dei dati del dashboard');
      console.error('Error loading dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  // Lambda per caricare gli atleti in base alla ricerca
  const loadAthletes = async (inputValue) => {
    if (!inputValue || inputValue.length < 2) {
      return [];
    }

    try {
      // Usa il metodo getUsers con filtro per atleti
      const usersResponse = await userService.getUsers({
        search: inputValue,
        limit: 10 // Limita i risultati per performance
      });

      if (usersResponse.success && usersResponse.data.items && usersResponse.data.items.length > 0) {
        return usersResponse.data.items.map(athlete => ({
          value: athlete.id,
          label: `${athlete.firstName} ${athlete.lastName} - ${athlete.email}`,
          athleteData: athlete
        }));
      } else {
        return [];
      }
    } catch (error) {
      console.error('Errore durante la ricerca atleti:', error);
      return [];
    }
  };

  // Lambda per gestire la selezione dell'atleta
  const handleAthleteSelection = (selectedOption) => {
    if (selectedOption) {
      // Redirect alla pagina di modifica dell'atleta
      navigate(`/users/${selectedOption.value}/edit`);
    }
  };

  // Lambda per caricare i gruppi in base alla ricerca
  const loadGroups = async (inputValue) => {
    if (!inputValue || inputValue.length < 2) {
      return [];
    }

    try {
      const groupsResponse = await groupService.getGroups({
        search: inputValue,
        academicYear: new Date().getFullYear(), // Anno accademico corrente
        limit: 10 // Limita i risultati per performance
      });
      
      if (groupsResponse.success && groupsResponse.data.items && groupsResponse.data.items.length > 0) {
        return groupsResponse.data.items.map(group => {
          const validSubscriptions = countValidSubscriptions(group.courseSubscriptions || []);
          return {
            value: group.id,
            label: `[${group.course.academicYear}/${group.course.academicYear + 1}] ${group.name} (${validSubscriptions} partecipanti)`,
            groupData: {
              ...group,
              participantsCount: validSubscriptions
            }
          };
        });
      } else {
        return [];
      }
    } catch (error) {
      console.error('Errore durante la ricerca gruppi:', error);
      return [];
    }
  };

  // Lambda per gestire la selezione del gruppo
  const handleGroupSelection = (selectedOption) => {
    if (selectedOption && selectedOption.groupData) {
      // Vai direttamente all'appello del gruppo selezionato
      navigate(`/groups/${selectedOption.groupData.id}/rollcall`);
    }
  };



  if (loading) {
    return (
      <div className="loading-spinner">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Caricamento...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="dashboard fade-in">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Dashboard</h2>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      {/* Layout principale: 3 Colonne come in foto */}
      <Row className="mb-4">
        {/* Colonna 1: Card monocontatore (Atleti Totali, Iscrizioni Anno Corrente, Gruppi Anno Corrente) */}
        <Col lg={4} className="mb-4 mb-lg-0">
          {/* Card Atleti Totali */}
          <Card 
            className="stat-card text-center position-relative mb-4" 
            style={{ cursor: 'pointer' }} 
            onClick={() => navigate('/users?role=athlete&academicYear=all')}
          >
            <div
              data-testid="atleti-icon"
              style={{
                position: 'absolute',
                top: '12px',
                left: '12px',
                width: '38px',
                height: '38px',
                backgroundColor: '#e2e9f9',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <i className="fas fa-user-plus text-primary" style={{ fontSize: '1.05rem' }}></i>
            </div>
            <Card.Body className="d-flex flex-column justify-content-center align-items-center py-4">
              <div className="stat-value text-dark fw-bold fs-2">{stats.totalUsers}</div>
              <div className="stat-label">Atleti Totali</div>
            </Card.Body>
          </Card>

          {/* Iscrizioni e Gruppi Anno Corrente */}
          <Row>
            <Col xs={6}>
              <Card 
                className="stat-card text-center position-relative h-100"
                style={{ borderBottom: '3px solid #f6c23e' }}
              >
                <div
                  data-testid="iscrizioni-icon"
                  style={{
                    position: 'absolute',
                    top: '10px',
                    left: '10px',
                    width: '32px',
                    height: '32px',
                    backgroundColor: '#faf2dd',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <i className="fas fa-clipboard-list" style={{ color: '#d97706', fontSize: '0.95rem' }}></i>
                </div>
                <Card.Body className="d-flex flex-column justify-content-center align-items-center py-3 px-1">
                  <div className="stat-value text-warning fw-bold fs-3">{stats.currentAcademicYearSubscriptions}</div>
                  <div className="stat-label" style={{ fontSize: '0.72rem' }}>Iscrizioni Anno Corrente</div>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={6}>
              <Card 
                className="stat-card text-center position-relative h-100"
                style={{ borderBottom: '3px solid #198754', cursor: 'pointer' }}
                onClick={() => navigate('/groups')}
              >
                <div
                  data-testid="gruppi-icon"
                  style={{
                    position: 'absolute',
                    top: '10px',
                    left: '10px',
                    width: '32px',
                    height: '32px',
                    backgroundColor: '#dee6e0',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <i className="fas fa-users text-success" style={{ fontSize: '0.95rem' }}></i>
                </div>
                <Card.Body className="d-flex flex-column justify-content-center align-items-center py-3 px-1">
                  <div className="stat-value text-success fw-bold fs-3">{stats.currentAcademicYearGroups}</div>
                  <div className="stat-label" style={{ fontSize: '0.72rem' }}>Gruppi Anno Corrente</div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Col>

        {/* Colonna 2: Certificati Medici & Abbonamenti */}
        <Col lg={4} className="mb-4 mb-lg-0">
          {/* Card Certificati Medici */}
          <Card className="stat-card mb-4">
            <Card.Header className="bg-white border-bottom py-2">
              <h6 className="mb-0 text-muted fw-bold">
                <i className="fas fa-file-medical me-2 text-primary"></i>
                Certificati Medici
              </h6>
            </Card.Header>
            <ListGroup variant="flush">
              <ListGroup.Item 
                action 
                className="d-flex justify-content-between align-items-center py-2 px-3"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate('/certificates?status=expiring')}
              >
                <span className="text-secondary small fw-semibold">
                  <i className="fas fa-hourglass-half text-success me-2"></i>
                  In Scadenza
                </span>
                <Badge bg="success" pill className="fs-6 px-3 py-1">
                  {stats.totalExpiringMedicalCertificates}
                </Badge>
              </ListGroup.Item>
              <ListGroup.Item 
                action 
                className="d-flex justify-content-between align-items-center py-2 px-3"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate('/certificates?status=expired')}
              >
                <span className="text-secondary small fw-semibold">
                  <i className="fas fa-exclamation-circle text-info me-2"></i>
                  Scaduti
                </span>
                <Badge bg="info" pill className="fs-6 px-3 py-1">
                  {stats.totalExpiredMedicalCertificates}
                </Badge>
              </ListGroup.Item>
              <ListGroup.Item 
                action 
                className="d-flex justify-content-between align-items-center py-2 px-3"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate('/users?certificateStatus=missing&role=athlete&academicYear=all')}
              >
                <span className="text-secondary small fw-semibold">
                  <i className="fas fa-times-circle text-danger me-2"></i>
                  Mancanti
                </span>
                <Badge bg="danger" pill className="fs-6 px-3 py-1">
                  {stats.totalMissingMedicalCertificates}
                </Badge>
              </ListGroup.Item>
            </ListGroup>
          </Card>

          {/* Card Abbonamenti */}
          <Card className="stat-card">
            <Card.Header className="bg-white border-bottom py-2">
              <h6 className="mb-0 text-muted fw-bold">
                <i className="fas fa-ticket-alt me-2 text-primary"></i>
                Abbonamenti
              </h6>
            </Card.Header>
            <ListGroup variant="flush">
              <ListGroup.Item 
                action 
                className="d-flex justify-content-between align-items-center py-2 px-3"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate('/subscriptions?status=expiring&academicYear=current&excludeTrials=true')}
              >
                <span className="text-secondary small fw-semibold">
                  <i className="fas fa-hourglass-half text-warning me-2"></i>
                  In Scadenza (30gg)
                </span>
                <Badge bg="warning" pill className="fs-6 px-3 py-1">
                  {stats.totalExpiringSubscriptions}
                </Badge>
              </ListGroup.Item>
              <ListGroup.Item 
                action 
                className="d-flex justify-content-between align-items-center py-2 px-3"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate('/subscriptions?status=expired&academicYear=current&excludeTrials=true')}
              >
                <span className="text-secondary small fw-semibold">
                  <i className="fas fa-calendar-times text-danger me-2"></i>
                  Scaduti
                </span>
                <Badge bg="danger" pill className="fs-6 px-3 py-1">
                  {stats.totalExpiredSubscriptions}
                </Badge>
              </ListGroup.Item>
              <ListGroup.Item 
                action 
                className="d-flex justify-content-between align-items-center py-2 px-3"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate('/subscriptions?status=unpaid')}
              >
                <span className="text-secondary small fw-semibold">
                  <i className="fas fa-money-bill-wave text-danger me-2"></i>
                  Non Pagati
                </span>
                <Badge bg="danger" pill className="fs-6 px-3 py-1">
                  {stats.totalUnpaidSubscriptions}
                </Badge>
              </ListGroup.Item>
              <ListGroup.Item 
                action 
                className="d-flex justify-content-between align-items-center py-2 px-3"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate('/subscriptions?status=running_out&academicYear=current&excludeTrials=true')}
              >
                <span className="text-secondary small fw-semibold">
                  <i className="fas fa-battery-half text-warning me-2"></i>
                  Ingressi in Esaurimento
                </span>
                <Badge bg="warning" pill className="fs-6 px-3 py-1">
                  {stats.totalRunningOutSubscriptions}
                </Badge>
              </ListGroup.Item>
              <ListGroup.Item 
                action 
                className="d-flex justify-content-between align-items-center py-2 px-3"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate('/subscriptions?status=sold_out&academicYear=current&excludeTrials=true')}
              >
                <span className="text-secondary small fw-semibold">
                  <i className="fas fa-battery-empty text-danger me-2"></i>
                  Ingressi Esauriti
                </span>
                <Badge bg="danger" pill className="fs-6 px-3 py-1">
                  {stats.totalSoldOutSubscriptions}
                </Badge>
              </ListGroup.Item>
            </ListGroup>
          </Card>
        </Col>

        {/* Colonna 3: Ricerca Rapida anagrafica e gruppi */}
        <Col lg={4}>
          {/* Ricerca Rapida anagrafica */}
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">
                <i className="fas fa-search me-2"></i>
                Cerca in Anagrafica
              </h5>
            </Card.Header>
            <Card.Body>
              <FilterSelect
                loadOptions={loadAthletes}
                onSelectionChange={handleAthleteSelection}
                placeholder="Cerca una persona per nome, cognome o email..."
                value={null} // Reset dopo ogni selezione
                noOptionsMessage="Nessuna persona trovata. Digita almeno 2 caratteri."
                loadingMessage="Ricerca persona in corso..."
                minInputLength={2}
                debounceDelay={500}
                className="athlete-search"
              />
            </Card.Body>
          </Card>

          {/* Ricerca Rapida Gruppi */}
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <i className="fas fa-users me-2"></i>
                Cerca Gruppi
              </h5>
            </Card.Header>
            <Card.Body>
              <FilterSelect
                loadOptions={loadGroups}
                onSelectionChange={handleGroupSelection}
                placeholder="Cerca un gruppo per nome..."
                value={null} // Reset dopo ogni selezione
                noOptionsMessage="Nessun gruppo trovato. Digita almeno 2 caratteri."
                loadingMessage="Ricerca gruppi in corso..."
                minInputLength={2}
                debounceDelay={500}
                className="group-search"
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Sezione Calendario Appelli del Giorno */}
      <Row>
        <Col xs={12}>
          <DailyRollCallsCalendar />
        </Col>
      </Row>

      {/* Sezione Lezioni Assegnate Coach */}
      <Row className="mt-4">
        <Col xs={12}>
          <CoachScheduleSection />
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;

